import {
  mysqlTable,
  int,
  bigint,
  tinyint,
  varchar,
  text,
  boolean,
  time,
  timestamp,
  mysqlEnum,
} from "drizzle-orm/mysql-core";
import { companies } from "./tenancy";
import { employeeShifts } from "./attendance";

/**
 * Per-company settings rows (one per company) — read before branching on feature behaviour
 * (reskin-docs/07 §7). attendance_settings ported here as part of Batch 1.1; the remaining
 * settings tables (company/leave/payroll/notification/currency_format) land in Part B.
 *
 * attendance_settings ported line-by-line from the base migration L235–264.
 * Deferred FK: default_employee_shift → employee_shifts (Batch 1.3).
 */
export const attendanceSettings = mysqlTable("attendance_settings", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  autoClockIn: mysqlEnum("auto_clock_in", ["yes", "no"]).default("no").notNull(),
  officeStartTime: time("office_start_time").notNull(),
  officeEndTime: time("office_end_time").notNull(),
  halfdayMarkTime: time("halfday_mark_time"),
  lateMarkDuration: tinyint("late_mark_duration").notNull(),
  clockinInDay: int("clockin_in_day").default(1).notNull(),
  employeeClockInOut: mysqlEnum("employee_clock_in_out", ["yes", "no"]).default("yes").notNull(),
  officeOpenDays: varchar("office_open_days", { length: 255 }).default("[1,2,3,4,5]").notNull(),
  ipAddress: text("ip_address"),
  radius: int("radius"),
  radiusCheck: mysqlEnum("radius_check", ["yes", "no"]).default("no").notNull(),
  ipCheck: mysqlEnum("ip_check", ["yes", "no"]).default("no").notNull(),
  alertAfter: int("alert_after"),
  alertAfterStatus: boolean("alert_after_status").default(true).notNull(),
  saveCurrentLocation: boolean("save_current_location").default(false).notNull(),
  defaultEmployeeShift: bigint("default_employee_shift", { mode: "number", unsigned: true })
    .default(1)
    .references(() => employeeShifts.id, { onDelete: "set null", onUpdate: "cascade" }),
  weekStartFrom: varchar("week_start_from", { length: 255 }).default("1").notNull(),
  allowShiftChange: boolean("allow_shift_change").default(true).notNull(),
  showClockInButton: mysqlEnum("show_clock_in_button", ["yes", "no"]).default("no").notNull(),
  // Added by later up() ALTER migrations (reconciled to final shape):
  autoClockInLocation: mysqlEnum("auto_clock_in_location", ["office", "home"])
    .default("office")
    .notNull(), // 2022_11_03
  monthlyReport: boolean("monthly_report").default(false).notNull(), // 2022_12_13
  monthlyReportRoles: varchar("monthly_report_roles", { length: 255 }), // 2022_12_13
  qrEnable: mysqlEnum("qr_enable", ["1", "0"]).default("1").notNull(), // 2024_03_22
  // NOTE: early_clock_in added (2023_03_21) then DROPPED (2023_05_16) → excluded.
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

/**
 * currency_format_settings — how ₹/currency is rendered per company (reskin-docs/07 §5).
 * Ported line-by-line from the base migration L679–687. NOTE: legacy table has **no
 * timestamps** — preserved.
 */
export const currencyFormatSettings = mysqlTable("currency_format_settings", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  currencyPosition: mysqlEnum("currency_position", [
    "left",
    "right",
    "left_with_space",
    "right_with_space",
  ])
    .default("left")
    .notNull(),
  noOfDecimal: int("no_of_decimal", { unsigned: true }).notNull(),
  thousandSeparator: varchar("thousand_separator", { length: 255 }),
  decimalSeparator: varchar("decimal_separator", { length: 255 }),
});

/**
 * leave_settings — per-company leave manager-approval mode (reskin-docs/07 §2). The
 * `manager_permission` value drives approval routing (pre-approve ⇒ two-step).
 * Ported line-by-line from 2022_09_13_075642_create_leave_settings_table.php L25–31.
 * (That migration also adds `leaves.manager_status_permission` — ported with leaves in 1.4.)
 */
export const leaveSettings = mysqlTable("leave_settings", {
  id: bigint("id", { mode: "number", unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  managerPermission: mysqlEnum("manager_permission", ["pre-approve", "approved", "cannot-approve"])
    .default("pre-approve")
    .notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});
